import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from "react";
import { apiUtils } from "../utils/apiUtils";
import { useUser } from "./UserContext";
import { useSubscription } from "../hooks/useSubscription";
import { queryClient } from '../services/queryClient';

const DataContext = createContext();

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error("useData must be used within a DataProvider");
  }
  return context;
};

export const DataProvider = ({ children }) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [clients, setClients] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { isAuthenticated, isLoading: userLoading, user } = useUser();
  const dashboardFetchInFlightRef = useRef(null);

  // Helper function to handle errors (don't set error state for auth errors)
  const handleError = useCallback((err, context) => {
    if (err.message !== 'Authentication required') {
      setError(err.message);
      console.error(`Error ${context}:`, err);
    }
  }, []);

  // Fetch dashboard data
  const fetchDashboardData = useCallback(async (timeframe = "30d") => {
    if (dashboardFetchInFlightRef.current === timeframe) return;
    dashboardFetchInFlightRef.current = timeframe;
    try {
      setLoading(true);
      setError(null);
      const response = await apiUtils.get(
        `/api/dashboard?timeframe=${timeframe}`
      );

      if (response.status === 304) {
        setLoading(false);
        return;
      }

      const data = await response.json();
      if (!data || typeof data !== "object") {
        setLoading(false);
        return;
      }

      if (data.success && data.data) {
        setDashboardData(data.data);
      } else {
        throw new Error(data.error || "Failed to fetch dashboard data");
      }
    } catch (err) {
      if (err.message !== "Authentication required") {
        setError(err.message);
        console.error("Error fetching dashboard data:", err);
      }
    } finally {
      dashboardFetchInFlightRef.current = null;
      setLoading(false);
    }
  }, []);

  const refreshClient = useCallback(async (clientId) => {
    if (!clientId) return;
    try {
      const response = await apiUtils.get(`/api/clients/${clientId}`);
      const data = await response.json();
      if (data.success && data.data) {
        setClients((prev) =>
          prev.map((c) => (c._id === (data.data._id || clientId) ? { ...c, ...data.data } : c))
        );
      }
    } catch {
      // A stale account count is tolerable; a failed patch must not surface.
    }
  }, []);

  // Fetch clients
  const fetchClients = useCallback(async (params = {}) => {
    try {
      setLoading(true);
      setError(null);
      const queryParams = new URLSearchParams(params);
      const response = await apiUtils.get(`/api/clients?${queryParams}`);
      const data = await response.json();

      if (data.success) {
        setClients(data.data.clients);
      } else {
        throw new Error(data.error || "Failed to fetch clients");
      }
    } catch (err) {
      // Only set error if it's not an auth error (auth errors are handled by apiUtils)
      if (err.message !== 'Authentication required') {
        setError(err.message);
        console.error("Error fetching clients:", err);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Create client
  const createClient = useCallback(async (clientData) => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiUtils.post("/api/clients", clientData);
      const data = await response.json();

      if (data.success) {
        setClients((prev) => [...prev, data.data]);
        return data.data;
      } else {
        throw new Error(data.error || "Failed to create client");
      }
    } catch (err) {
      handleError(err, "creating client");
      throw err;
    } finally {
      setLoading(false);
    }
  }, [handleError]);

  // Update client
  const updateClient = useCallback(async (id, clientData) => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiUtils.put(`/api/clients/${id}`, clientData);
      const data = await response.json();

      if (data.success) {
        setClients((prev) =>
          prev.map((client) => (client._id === id ? data.data : client))
        );
      } else {
        throw new Error(data.error || "Failed to update client");
      }
    } catch (err) {
      handleError(err, "updating client");
      throw err;
    } finally {
      setLoading(false);
    }
  }, [handleError]);

  // Delete client
  const deleteClient = useCallback(async (id, confirmName) => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiUtils.delete(`/api/clients/${id}`, { data: { confirmName } });
      const data = await response.json();

      if (response.status === 202 && data.success && data.data?.operationId) {
        setClients((prev) => prev.filter((client) => client._id !== id));
        setAccounts((prev) => prev.filter((account) => String(account.client?._id || account.client) !== String(id)));
        setDashboardData(null);
        queryClient.invalidateQueries();
        return data.data;
      } else {
        throw new Error(data.error || "Failed to delete client");
      }
    } catch (err) {
      handleError(err, "deleting client");
      throw err;
    } finally {
      setLoading(false);
    }
  }, [handleError]);

  // Fetch accounts
  const fetchAccounts = useCallback(async (params = {}) => {
    try {
      setLoading(true);
      setError(null);
      const queryParams = new URLSearchParams(params);
      const response = await apiUtils.get(`/api/accounts?${queryParams}`);
      const data = await response.json();

      if (data.success) {
        setAccounts(data.data.accounts);
      } else {
        throw new Error(data.error || "Failed to fetch accounts");
      }
    } catch (err) {
      // Only set error if it's not an auth error (auth errors are handled by apiUtils)
      if (err.message !== 'Authentication required') {
        setError(err.message);
        console.error("Error fetching accounts:", err);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch accounts for a specific client
  const fetchAccountsByClient = useCallback(async (clientId) => {
    try {
      // Don't set global loading state - let components manage their own loading
      const response = await apiUtils.get(`/api/accounts/client/${clientId}`);
      const data = await response.json();

      if (data.success) {
        return data.data.accounts;
      } else {
        throw new Error(data.error || "Failed to fetch client accounts");
      }
    } catch (err) {
      // Only set error if it's not an auth error (auth errors are handled by apiUtils)
      if (err.message !== 'Authentication required') {
        console.error("Error fetching client accounts:", err);
      }
      return [];
    }
  }, []);

  // Create account
  const createAccount = useCallback(async (accountData) => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiUtils.post("/api/accounts", accountData);
      const data = await response.json();

      if (data.success) {
        setAccounts((prev) => [...prev, data.data]);
        refreshClient(data.data?.client);
        return data.data;
      } else {
        throw new Error(data.error || "Failed to create account");
      }
    } catch (err) {
      handleError(err, "creating account");
      throw err;
    } finally {
      setLoading(false);
    }
  }, [handleError, refreshClient]);

  // Update account
  const updateAccount = useCallback(async (id, accountData) => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiUtils.put(`/api/accounts/${id}`, accountData);
      const data = await response.json();

      if (data.success) {
        setAccounts((prev) =>
          prev.map((account) => (account._id === id ? data.data : account))
        );
        refreshClient(data.data?.client);
      } else {
        throw new Error(data.error || "Failed to update account");
      }
    } catch (err) {
      handleError(err, "updating account");
      throw err;
    } finally {
      setLoading(false);
    }
  }, [handleError, refreshClient]);

  // Delete account
  const deleteAccount = useCallback(async (id, identity) => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiUtils.delete(`/api/accounts/${id}`, { data: {
        confirmHandle: identity?.handle,
        confirmPlatform: identity?.platform,
      } });
      const data = await response.json();

      if (response.status === 202 && data.success && data.data?.operationId) {
        setAccounts((prev) => prev.filter((account) => account._id !== id));
        setDashboardData(null);
        queryClient.invalidateQueries();
        return data.data;
      } else {
        throw new Error(data.error || "Failed to delete account");
      }
    } catch (err) {
      handleError(err, "deleting account");
      throw err;
    } finally {
      setLoading(false);
    }
  }, [handleError]);

  // Refresh account data
  const refreshAccountData = useCallback(async (id) => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiUtils.post(`/api/accounts/${id}/refresh`);
      const data = await response.json();

      if (data.success) {
        setAccounts((prev) =>
          prev.map((account) => (account._id === id ? data.data : account))
        );
        refreshClient(data.data?.client);
        return data.data;
      } else {
        throw new Error(data.error || "Failed to refresh account data");
      }
    } catch (err) {
      handleError(err, "refreshing account data");
      throw err;
    } finally {
      setLoading(false);
    }
  }, [handleError, refreshClient]);

  // Reset data when user logs out
  const resetData = useCallback(() => {
    setDashboardData(null);
    setClients([]);
    setAccounts([]);
    setError(null);
  }, []);

  // Listen for auth logout events and reset data
  useEffect(() => {
    const handleAuthLogout = () => {
      console.log('[DataContext] User logged out, resetting data');
      resetData();
    };

    window.addEventListener('auth-logout', handleAuthLogout);
    return () => window.removeEventListener('auth-logout', handleAuthLogout);
  }, [resetData]);

  // Handle real-time scan list updates via WebSocket
  const handleScanListUpdate = useCallback((message) => {
    if (message.type === 'scan.list.updated') {
      const { action, scan, scanId, status } = message.payload;

      console.log('[DataContext] Received scan list update:', message.payload);

      if (action === 'created' && scan) {
        // Add new scan to dashboard data
        setDashboardData(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            recentScans: [scan, ...(prev.recentScans || [])].slice(0, 5), // Keep latest 5
            totalScans: (prev.totalScans || 0) + 1,
          };
        });
      } else if (action === 'updated' && scanId) {
        // Update existing scan status in dashboard data
        setDashboardData(prev => {
          if (!prev?.recentScans) return prev;

          const updatedScans = prev.recentScans.map(existingScan =>
            existingScan._id === scanId
              ? { ...existingScan, status }
              : existingScan
          );

          return {
            ...prev,
            recentScans: updatedScans,
          };
        });
      }
    }
  }, []);

  // Subscribe to the WORKSPACE's scan updates. The backend broadcasts
  // scan.list.updated to user:{scan.owner}:scans, and scan.owner is always the
  // workspace owner - so a team member subscribing under their own id heard
  // nothing. Root users carry teamOwner === their own id, so this is the
  // owner's topic for everyone.
  const workspaceId = user?.teamOwner || user?.id;
  const userTopic = workspaceId ? `user:${workspaceId}:scans` : null;
  useSubscription(userTopic, handleScanListUpdate, { enabled: !!workspaceId });

  // Initialize data on mount - only when user is authenticated
  useEffect(() => {
    // Don't fetch data if user context is still loading
    if (userLoading) {
      return;
    }

    // Only fetch data if user is authenticated
    if (isAuthenticated()) {
      // Inline the API calls to avoid function dependencies (dashboard is fetched by Dashboard page via fetchDashboardData)
      const loadData = async () => {
        try {
          setLoading(true);
          setError(null);

          // Fetch clients
          const clientsResponse = await apiUtils.get('/api/clients');
          if (clientsResponse.ok) {
            const clientsData = await clientsResponse.json();
            setClients(clientsData.data.clients);
          }

          // Fetch accounts
          const accountsResponse = await apiUtils.get('/api/accounts');
          if (accountsResponse.ok) {
            const accountsData = await accountsResponse.json();
            setAccounts(accountsData.data.accounts);
          }
        } catch (err) {
          if (err.message !== 'Authentication required') {
            setError(err.message);
            console.error("Error loading data:", err);
          }
        } finally {
          setLoading(false);
        }
      };

      loadData();
    } else {
      // Reset data if user is not authenticated
      setDashboardData(null);
      setClients([]);
      setAccounts([]);
      setError(null);
    }
    // Reacts to auth settling, not to every isAuthenticated identity change;
    // the branch reads isAuthenticated at that moment, which is the intent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userLoading]);

  const value = useMemo(() => ({
    dashboardData,
    clients,
    accounts,
    loading,
    error,
    fetchDashboardData,
    fetchClients,
    createClient,
    updateClient,
    deleteClient,
    fetchAccounts,
    fetchAccountsByClient,
    createAccount,
    updateAccount,
    deleteAccount,
    refreshAccountData,
    resetData,
  }), [
    dashboardData,
    clients,
    accounts,
    loading,
    error,
    fetchDashboardData,
    fetchClients,
    createClient,
    updateClient,
    deleteClient,
    fetchAccounts,
    fetchAccountsByClient,
    createAccount,
    updateAccount,
    deleteAccount,
    refreshAccountData,
    resetData,
  ]);

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};
